import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SVG from 'react-inlinesvg';
import styled from 'styled-components';

import { ArrowLeftSvg } from '../../../../assets/icons';
import theme from '../../../../assets/theme';
import { useMetrics } from '../../../../hooks/useMetrics';
import { IconWrapper } from '../../../_atoms/IconWrapper';
import { Slide } from '../../../SlideComponents/slide.types';
import {
  createFigureElement,
  createShapeElement,
  replacefigureColors,
} from '../../../SlideComponents/slideUtil';
import figures, { Figure } from './figures';
import shapes from './shapes';

const FigureElementTab = ({
  onRoundChange,
  round,
}: {
  onRoundChange: (round: Slide) => void;
  round?: Slide;
}) => {
  const [addElementState, setAddElementState] = useState<null | 'shape' | 'svg'>(null);
  const { t } = useTranslation('Host');
  const metrics = useMetrics();
  const handleAddShape = useCallback(
    ({
      clipPath,
      height = 200,
      width = 200,
      name,
    }: {
      clipPath?: string;
      defaultColors: string[];
      height?: number;
      width?: number;
      name: string;
    }) => {
      if (!round) return;
      metrics.logEvent('Creator.ElementTab.Add', { type: 'shape', shapeType: name });
      const newShapeElement = createShapeElement({
        height,
        width,
        clipPath,
      });

      onRoundChange({
        ...round,
        elements: [...round.elements, newShapeElement],
      });
    },
    [metrics, onRoundChange, round],
  );

  const handleAddFigure = useCallback(
    ({ value, height = 200, width = 200, colors, name }: Figure) => {
      if (!round) return;
      metrics.logEvent('Creator.ElementTab.Add', { type: 'sticker', stickerType: name });
      // random x position in the center of the slide

      const x = 1920 / 2 - width / 2 - 50 + Math.random() * 100;
      const y = 1080 / 2 - height / 2 - 50 + Math.random() * 100;
      const newFigureElement = createFigureElement({
        height,
        width,
        x,
        y,
        value,
        defaultColors: colors,
      });

      onRoundChange({
        ...round,
        elements: [...round.elements, newFigureElement],
      });
    },
    [metrics, onRoundChange, round],
  );
  if (addElementState === 'shape')
    return (
      <FiguresElementContainer>
        <Title
          style={{ marginBottom: 10, cursor: 'pointer' }}
          $fontSize={20}
          onClick={() => setAddElementState(null)}
        >
          <IconWrapper>
            <ArrowLeftSvg />
          </IconWrapper>
          {t('Add shape')}
        </Title>
        <AllItemsContainer>
          {shapes.map((shape, i) => (
            <FigureButton key={i} onClick={() => handleAddShape(shape)}>
              <ShapeIcon
                $clipPath={shape.clipPath}
                style={{ height: shape?.height || '100%' }}
              />
            </FigureButton>
          ))}
        </AllItemsContainer>
      </FiguresElementContainer>
    );
  if (addElementState === 'svg')
    return (
      <FiguresElementContainer>
        <Title
          style={{ marginBottom: 10, cursor: 'pointer' }}
          $fontSize={20}
          onClick={() => setAddElementState(null)}
        >
          <IconWrapper>
            <ArrowLeftSvg />
          </IconWrapper>
          {t('Add sticker')}
        </Title>
        <AllItemsContainer>
          {figures.map((figure, i) => (
            <FigureButton key={i} onClick={() => handleAddFigure(figure)}>
              <SVG
                src={replacefigureColors(figure.value, figure.colors)}
                width={'100%'}
                height={'100%'}
                key={i}
              />
            </FigureButton>
          ))}
        </AllItemsContainer>
      </FiguresElementContainer>
    );

  return (
    <FiguresElementContainer>
      <Title style={{ marginBottom: 10 }} $fontSize={20}>
        {t('Add element')}
      </Title>
      <SubTitleContainer>
        <SubTitle>{t('Shapes')}</SubTitle>
        <ShowMore onClick={() => setAddElementState('shape')}>{'Show more'}</ShowMore>
      </SubTitleContainer>
      <ItemsContainer>
        {shapes.slice(0, 10).map((shape, i) => (
          <FigureButton key={i} onClick={() => handleAddShape(shape)}>
            <ShapeIcon
              $clipPath={shape.clipPath}
              style={{ height: shape?.height || '100%' }}
            />
          </FigureButton>
        ))}
      </ItemsContainer>
      <SubTitleContainer>
        <SubTitle>{t('Stickers')}</SubTitle>
        <ShowMore onClick={() => setAddElementState('svg')}>{'Show more'}</ShowMore>
      </SubTitleContainer>

      <ItemsContainer>
        {figures.slice(0, 10).map((figure, i) => (
          <FigureButton key={i} onClick={() => handleAddFigure(figure)}>
            <SVG
              src={replacefigureColors(figure.value, figure.colors)}
              width={'100%'}
              height={'100%'}
              key={i}
            />
          </FigureButton>
        ))}
      </ItemsContainer>
    </FiguresElementContainer>
  );
};

const FigureButton = styled.div`
  width: 80px;
  height: 80px;
  border: none;
  transform: scale(0.7);
  box-shadow: none;
  margin: 0;
  position: relative;
  transition: all 0.2s ease-in-out;
  border-radius: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  &:hover {
    cursor: pointer;
    background-color: ${theme.colors.greyLight};
  }
  svg {
    margin: 0;
  }
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;
const ShapeIcon = styled.div<{ $clipPath?: string }>`
  clip-path: ${({ $clipPath }) => $clipPath || 'none'};
  width: 100%;
  height: 100%;
  background-color: ${theme.colors.greyDark};
`;

const ItemsContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 80px;
  overflow-x: scroll;
  justify-content: flex-start;
  margin: auto;
  width: 100%;
`;

const AllItemsContainer = styled.div`
  display: grid;
  margin: auto;
  grid-template-columns: repeat(auto-fit, minmax(75px, 1fr));
  grid-template-rows: repeat(auto-fit, minmax(75px, 1fr));
  width: 100%;
  gap: 10px;
  max-height: 95%;
  overflow-y: auto;
  justify-content: center;
`;

const SubTitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
`;
const SubTitle = styled.h4`
  margin-top: 10px;
`;
const ShowMore = styled.h4`
  cursor: pointer;
`;

const Title = styled.h3<{ $fontSize: number }>`
  text-align: left;
  width: 100%;
  font-weight: bold;
  font-size: ${({ $fontSize }) => $fontSize}px;
  line-height: 140%;
  color: ${theme.colors.greyVeryDark};
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  margin-bottom: 0px;

  @media (max-width: 690px) {
    font-size: ${({ $fontSize }) => $fontSize - 5}px;
  }
`;

const FiguresElementContainer = styled.div`
  padding: 20px;
  height: 100%;
  position: relative;
`;
export default FigureElementTab;
